import { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, CheckCircle, Calendar, Youtube, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import CalendlyButton from '../components/CalendlyButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-[#1e3a5f] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-[#c9a227] uppercase tracking-[0.2em] text-sm font-semibold mb-4">
              Contact
            </p>
            <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              Nous Contacter
            </h1>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Une question, un projet ? N'hésitez pas à nous contacter. 
              Nous vous répondrons dans les plus brefs délais.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-6 md:mb-8">
                Informations de Contact
              </h2>
              
              <div className="space-y-4 md:space-y-6 mb-8 md:mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#f8f9fa] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#c9a227]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1 text-sm md:text-base">Email</h3>
                    <a href="mailto:gdivision.sas@gmail.com" className="text-gray-600 hover:text-[#c9a227] transition-colors text-sm md:text-base break-all">
                      gdivision.sas@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#f8f9fa] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#c9a227]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1 text-sm md:text-base">Téléphone</h3>
                    <a href="tel:+33645040975" className="text-gray-600 hover:text-[#c9a227] transition-colors text-sm md:text-base">
                      +33 6 45 04 09 75
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#f8f9fa] rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#c9a227]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1 text-sm md:text-base">Localisation</h3>
                    <span className="text-gray-600 text-sm md:text-base">France</span>
                  </div>
                </div>
              </div>

              {/* Calendly CTA */}
              <div className="bg-[#1e3a5f] p-6 md:p-8 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="text-[#c9a227]" size={24} />
                  <h3 className="font-['Playfair_Display'] text-lg md:text-xl font-semibold text-white">
                    Prendre Rendez-vous
                  </h3>
                </div>
                <p className="text-gray-300 text-sm md:text-base mb-6">
                  Réservez directement un créneau pour une consultation gratuite.
                </p>
                <CalendlyButton
                  url="https://calendly.com/gdivision/rdv"
                  text="Réserver un Créneau"
                  className="bg-[#c9a227] text-white hover:bg-[#b08d22] rounded-none px-6 py-3 uppercase tracking-wider text-xs md:text-sm font-semibold w-full transition-colors text-center block cursor-pointer"
                  data-testid="calendly-contact-btn"
                />
              </div>

              {/* WhatsApp CTA */}
              <div className="bg-[#f8f9fa] p-6 md:p-8">
                <h3 className="font-['Playfair_Display'] text-lg md:text-xl font-semibold text-[#1e3a5f] mb-4">
                  Contact Rapide via WhatsApp
                </h3>
                <p className="text-gray-600 text-sm md:text-base mb-6">
                  Pour une réponse rapide, contactez-nous directement sur WhatsApp.
                </p>
                <a 
                  href="https://wa.me/33645040975"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    className="bg-[#25D366] text-white hover:bg-[#1da851] rounded-none px-6 py-3 uppercase tracking-wider text-xs md:text-sm font-semibold w-full"
                    data-testid="contact-whatsapp-btn"
                  >
                    Ouvrir WhatsApp
                  </Button>
                </a>
              </div>

              {/* Social Links */}
              <div className="mt-6 flex items-center gap-4">
                <a 
                  href="https://www.youtube.com/@G10visions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center hover:bg-[#c9a227] transition-colors text-white"
                >
                  <Youtube size={18} />
                </a>
                <a 
                  href="https://www.linkedin.com/company/111674288"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center hover:bg-[#c9a227] transition-colors text-white"
                >
                  <Linkedin size={18} />
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-6 md:mb-8">
                Envoyez-nous un Message
              </h2>

              {success ? (
                <div className="bg-green-50 border border-green-200 p-6 md:p-8 text-center" data-testid="contact-success">
                  <CheckCircle className="text-green-500 mx-auto mb-4" size={48} />
                  <h3 className="font-['Playfair_Display'] text-lg md:text-xl font-semibold text-green-700 mb-2">
                    Message Envoyé !
                  </h3>
                  <p className="text-green-600 text-sm md:text-base">
                    Nous avons bien reçu votre message et vous répondrons rapidement.
                  </p>
                  <Button 
                    onClick={() => setSuccess(false)}
                    variant="outline"
                    className="mt-6 border-green-500 text-green-600 hover:bg-green-500 hover:text-white rounded-none"
                  >
                    Envoyer un autre message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#1e3a5f] font-medium text-sm">
                        Nom complet *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="rounded-none border-gray-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] text-sm"
                        placeholder="Votre nom"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#1e3a5f] font-medium text-sm">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="rounded-none border-gray-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] text-sm"
                        placeholder="votre@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#1e3a5f] font-medium text-sm">
                        Téléphone
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="rounded-none border-gray-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] text-sm"
                        placeholder="06 XX XX XX XX"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-[#1e3a5f] font-medium text-sm">
                        Sujet *
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="rounded-none border-gray-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] text-sm"
                        placeholder="Sujet de votre message"
                        data-testid="contact-subject-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#1e3a5f] font-medium text-sm">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="rounded-none border-gray-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] resize-none text-sm"
                      placeholder="Décrivez votre projet ou posez votre question..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm">{error}</p>
                  )}

                  <Button 
                    type="submit"
                    disabled={loading}
                    className="bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none px-8 py-3 md:py-4 uppercase tracking-wider text-xs md:text-sm font-semibold w-full sm:w-auto"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full loading-spinner"></div>
                        Envoi...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Send size={16} />
                        Envoyer
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
